import { useCallback, useEffect, useState } from 'react';
import { App, Drawer, Empty, Typography } from 'antd';
import { getSpokeOversight } from '@/api/oversight';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { SpokeOversightDetail } from '@/api/types';
import { MetricCell } from './badges';
import { DefensiveRow } from './DefensiveRow';

const { Title, Text } = Typography;

function Section({
  title,
  count,
  kind,
  rows,
  emptyText,
}: {
  title: string;
  count: number;
  kind: 'benign' | 'breach' | 'signal';
  rows: Array<Record<string, unknown>>;
  emptyText: string;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="ol-section" style={{ marginBottom: 8 }}>
        {title}
        <span className="ol-section__count">
          <MetricCell value={count} kind={kind} />
        </span>
      </div>
      {rows.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {emptyText}
        </Text>
      ) : (
        rows.map((r, i) => <DefensiveRow key={i} row={r} />)
      )}
    </div>
  );
}

/** Per-spoke oversight drill-down (F-TA-02): case breakdown, SLA breaches, recon breaks, reviews. */
export function SpokeDrawer({
  spokeId,
  displayName,
  onClose,
}: {
  spokeId: string | null;
  displayName?: string;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [detail, setDetail] = useState<SpokeOversightDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setUnavailable(false);
    setDetail(null);
    try {
      setDetail(await getSpokeOversight(id));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (spokeId) void load(spokeId);
  }, [spokeId, load]);

  const byStatus = detail ? Object.entries(detail.cases.byStatus ?? {}) : [];

  return (
    <Drawer
      open={Boolean(spokeId)}
      onClose={onClose}
      width={600}
      loading={loading}
      title={
        <span>
          <span className="ol-mono">{spokeId}</span>
          {displayName && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              {displayName}
            </Text>
          )}
        </span>
      }
    >
      {unavailable && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Spoke drill-down is not live yet (Track A oversight API pending)."
        />
      )}
      {detail && (
        <div>
          <div className="ol-card" style={{ marginBottom: 18 }}>
            <div className="ol-section" style={{ marginBottom: 10 }}>
              Cases
            </div>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: byStatus.length ? 12 : 0 }}>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {detail.cases.open.toLocaleString()}
                </Title>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Open cases
                </Text>
              </div>
              <div>
                {/* null = entity graph not provisioned — render "—" honestly, never a faked 0. */}
                <Title
                  level={3}
                  style={{ margin: 0 }}
                  title={
                    detail.entityCount == null
                      ? 'No source wired — the entity graph is not provisioned in this deployment.'
                      : undefined
                  }
                >
                  {detail.entityCount == null ? '—' : detail.entityCount.toLocaleString()}
                </Title>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Entities
                </Text>
              </div>
            </div>
            {byStatus.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {byStatus.map(([status, n]) => (
                  <span
                    key={status}
                    className="ol-chip"
                    title={`${status}: ${n}`}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {status}
                    <b style={{ marginLeft: 4 }}>{Number(n).toLocaleString()}</b>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Section
            title="SLA breaches"
            count={detail.slaBreaches.length}
            kind="breach"
            rows={detail.slaBreaches}
            emptyText="No SLA breaches for this spoke."
          />
          <Section
            title="Reconciliation breaks"
            count={detail.reconciliationBreaks.length}
            kind="breach"
            rows={detail.reconciliationBreaks}
            emptyText="No reconciliation breaks."
          />
          <Section
            title="Recent reviews"
            count={detail.recentReviews.length}
            kind="benign"
            rows={detail.recentReviews}
            emptyText="No recent reviews recorded."
          />
        </div>
      )}
    </Drawer>
  );
}
